// src/components/pages/ResultsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { Fieldset } from 'primereact/fieldset';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Slider } from 'primereact/slider';
import { fetchSearchData, clients } from './DataFlow';
import { useLocation } from 'react-router-dom';

const ResultsTab = () => {
    const location = useLocation();
    const [searchData, setSearchData] = useState([]);
    const [client, setClient] = useState(null);
    const [version, setVersion] = useState('');
    const [suite, setSuite] = useState('');
    const [branch, setBranch] = useState('');
    const [dates, setDates] = useState(null);
    const [passRange, setPassRange] = useState([25, 100]);
    const [expandedKeys, setExpandedKeys] = useState({});

    // Memoize the getIdParam function
    const getIdParam = useCallback(() => {
        const searchParams = new URLSearchParams(location.search); // Parse the search string
        const id = searchParams.get('id'); // Retrieve the 'id' query parameter
        return id;
    }, [location.search]);

    // Memoize the handleClick function
    const handleClick = useCallback(
        (event, dataIdPassed) => {
            const dataIdForward = dataIdPassed ? dataIdPassed : null;
            fetchSearchData({ client, version, suite, branch, dates, passRange, dataIdForward }, setSearchData);
        },
        [client, version, suite, branch, dates, passRange]
    );

    useEffect(() => {
        const id = getIdParam();
        if (id) {
            handleClick(null, id);
        }
    }, [getIdParam, handleClick]);

    const handleClear = () => {
        setSearchData([]);
        setVersion('');
        setBranch('');
        setPassRange([0, 100]);
        setDates(null);
        setClient('');
        setSuite('');
    };

    const expandAll = () => {
        let _expandedKeys = {};
        const expandNode = (node) => {
            if (node.children) {
                _expandedKeys[node.key] = true;
                node.children.forEach((child) => expandNode(child));
            }
        };

        searchData.forEach((node) => expandNode(node));
        setExpandedKeys(_expandedKeys);
    };

    const collapseAll = () => {
        setExpandedKeys({});
    };

    const rowClassName = (node) => {
        return {
            'row-passed': node.data.status === 'passed',
            'row-failed': node.data.status === 'failed',
            'row-warning':
                node.data.status === 'broken' ||
                node.data.status === 'unknown' ||
                node.data.status === 'skipped',
        };
    };

    return (
        <div >
            <Fieldset legend="Filters" toggleable className="filter-style">
                <Dropdown
                    className="search-box"
                    value={client}
                    options={clients}
                    onChange={(e) => setClient(e.value)}
                    placeholder="Select Client"
                />
                <InputText
                    placeholder="Version"
                    className="search-box"
                    id="versionValue"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                />
                <InputText
                    placeholder="Suite"
                    className="search-box"
                    id="suiteValue"
                    value={suite}
                    onChange={(e) => setSuite(e.target.value)}
                />
                <InputText
                    placeholder="Branch name"
                    className="search-box"
                    id="branchValue"
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                />
                <Calendar
                    placeholder="Range Dates"
                    className="search-box"
                    id="dateRangeValue"
                    selectionMode="range"
                    readOnlyInput
                    dateFormat="dd/mm/yy"
                    value={dates}
                    onChange={(e) => setDates(e.value)}
                ></Calendar>

                <Card title="Pass Percentage">
                    <Slider
                        value={passRange}
                        onChange={(e) => setPassRange(e.value)}
                        className="w-14rem range-slider"
                        range
                    />
                    <InputText
                        className="search-box-mini"
                        id="passPercetangeRangeValue"
                        value={`${passRange[0]}-${passRange[1]}`}
                        readOnly
                        onChange={(e) => setBranch(e.target.value)}
                    />
                </Card>
                <div className="filter-buttons">
                    <Button className="search-box-small" label="Filter" onClick={handleClick} />
                    <Button className="search-box-small" label="Clear" onClick={handleClear} />
                    <Button className="search-box-small" label="Expand All" onClick={expandAll} />
                    <Button className="search-box-small" label="Collapse All" onClick={collapseAll} />
                </div>
            </Fieldset>

            <TreeTable
                value={searchData}
                expandedKeys={expandedKeys}
                rowClassName={rowClassName}
                onToggle={(e) => setExpandedKeys(e.value)}
                sortable
                tableStyle={{ minWidth: '50rem' }}
                className="dark-table"
            >
                <Column
                    field="name"
                    header="Name"
                    expander
                    filter
                    filterPlaceholder="Filter by name"
                    sortable
                    style={{ width: '35%' }}
                ></Column>
                <Column
                    field="parameters"
                    header="Parameters"
                    filter
                    filterPlaceholder="Filter by params"
                    sortable
                    style={{ width: '20%' }}
                ></Column>
                <Column
                    field="time_start"
                    header="Start Time"
                    filter
                    filterPlaceholder="Filter by start time"
                    sortable
                    style={{ width: '12.5%' }}
                ></Column>
                <Column
                    field="time_stop"
                    header="Stop Time"
                    filter
                    filterPlaceholder="Filter by stop time"
                    sortable
                    style={{ width: '12.5%' }}
                ></Column>
                <Column
                    field="duration"
                    header="Duration (s)"
                    filter
                    filterPlaceholder="Filter by duration"
                    sortable
                    style={{ width: '10%' }}
                ></Column>
                <Column
                    field="status"
                    header="Status"
                    filter
                    filterPlaceholder="Filter by status"
                    sortable
                    style={{ width: '10%' }}
                ></Column>
            </TreeTable>
        </div>
    );
};

export default ResultsTab;
